// Floors within a scene and a token's height (qa-runs#1160; design/32). A scene's floors are its settings'
// (`scene.floors`, set by the DM's `scene.set`), listed bottom first, at most 10; a scene with none reads as one floor,
// *Ground*, base 0 (an old scene plays exactly as before). A thing's floor is `object.floor` (absent: the ground floor);
// a token's height is `object.z` in squares above the scene's ground (absent: standing on its floor). A player's floors
// are the floors their tokens are on: nothing of another floor — its name, things or walls — reaches them.
export const FLOOR_LIMITS = { floors: 10, name: 40, height: 2000, openings: 400 };
export const GROUND_ID = 'ground';
/** What an old scene reads as: one floor, Ground, no ceiling. */
export const DEFAULT_FLOORS = [{ id: GROUND_ID, name: 'Ground', height: 2, ceiling: false, ground: true }];
const isId = (v) => typeof v === 'string' && /^[a-z0-9-]{1,40}$/.test(v);
/** A scene's floors from the wire or a snapshot, strict: 1–10, unique ids, names 1–40 characters, heights in squares. Null when malformed. */
export function parseFloors(v) {
    if (!Array.isArray(v) || v.length < 1 || v.length > FLOOR_LIMITS.floors)
        return null;
    const out = [];
    const ids = new Set();
    let grounds = 0;
    for (const f of v) {
        if (typeof f !== 'object' || f === null)
            return null;
        const r = f;
        if (!isId(r.id) || ids.has(r.id))
            return null;
        if (typeof r.name !== 'string' || !r.name.trim() || r.name.length > FLOOR_LIMITS.name)
            return null;
        if (typeof r.height !== 'number' || !Number.isFinite(r.height) || r.height <= 0 || r.height > FLOOR_LIMITS.height)
            return null;
        if (typeof r.ceiling !== 'boolean')
            return null;
        if (r.open !== undefined && typeof r.open !== 'boolean')
            return null;
        if (r.ground !== undefined && typeof r.ground !== 'boolean')
            return null;
        if (r.ground === true)
            grounds += 1;
        let map;
        if (r.map !== undefined && r.map !== null) {
            const m = parseFloorMap(r.map);
            if (!m)
                return null;
            map = m;
        }
        let openings;
        if (r.openings !== undefined) {
            if (!Array.isArray(r.openings) || r.openings.length > FLOOR_LIMITS.openings || !r.openings.every((k) => typeof k === 'string' && OPENING.test(k)))
                return null;
            openings = [...new Set(r.openings)].sort();
        }
        ids.add(r.id);
        out.push({ id: r.id, name: r.name.trim(), height: r.height, ceiling: r.ceiling, ...(r.open === true ? { open: true } : {}), ...(r.ground === true ? { ground: true } : {}), ...(map ? { map } : {}), ...(openings?.length ? { openings } : {}) });
    }
    return grounds <= 1 ? out : null;
}
const OPENING = /^\d{1,4},\d{1,4}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isSide = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 100_000;
/** A floor's map from the wire: an asset id (its name and size are the server's stamp; the DM's browser sends what it knows). */
export function parseFloorMap(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const m = v;
    if (typeof m.id !== 'string' || !UUID.test(m.id))
        return null;
    return { id: m.id, name: typeof m.name === 'string' ? m.name.slice(0, 200) : '', width: isSide(m.width) ? m.width : 1, height: isSide(m.height) ? m.height : 1 };
}
export const copyFloors = (fs) => fs.map((f) => ({ ...f, ...(f.map ? { map: { ...f.map } } : {}), ...(f.openings ? { openings: [...f.openings] } : {}) }));
/** The scene's floors: its own, or the one Ground an old scene reads as. */
export const sceneFloors = (scene) => (scene?.floors?.length ? scene.floors : DEFAULT_FLOORS);
/** Whether the scene has more than one floor: the only time floors change what anyone sees. */
export const hasFloors = (scene) => (scene?.floors?.length ?? 0) > 1;
/** The ground floor's id: the one marked, else the first. */
export const groundFloor = (floors) => (floors.find((f) => f.ground) ?? floors[0] ?? DEFAULT_FLOORS[0]).id;
/** A thing's floor: its own while the scene has it, else the ground floor. */
export function floorOf(o, floors) {
    return o.floor && floors.some((f) => f.id === o.floor) ? o.floor : groundFloor(floors);
}
/** Where a floor starts, in squares above the scene's ground: the ground floor at 0, those above it on top, those below under it. A player's copy says its own. */
export function floorBase(floors, id) {
    const at = floors.findIndex((f) => f.id === id);
    if (at < 0)
        return 0;
    if (typeof floors[at].base === 'number')
        return floors[at].base;
    const g = Math.max(0, floors.findIndex((f) => f.id === groundFloor(floors)));
    let base = 0;
    if (at >= g)
        for (let k = g; k < at; k += 1)
            base += floors[k].height;
    else
        for (let k = at; k < g; k += 1)
            base -= floors[k].height;
    return base;
}
/** A thing's height above the scene's ground, in squares: its own, or standing on its floor. */
export function elevation(o, scene) {
    if (typeof o.z === 'number')
        return o.z;
    if (!hasFloors(scene))
        return 0;
    const floors = sceneFloors(scene);
    return floorBase(floors, floorOf(o, floors));
}
/** A height a token may be given: finite, within ±(10 floors of the tallest) squares. */
export const isHeight = (v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= FLOOR_LIMITS.height * FLOOR_LIMITS.floors;
const memo = new WeakMap();
/** The floors a player's tokens are on (design/32 §4): what they may see of the scene; the ground floor with none. Memoised on the objects record. */
export function viewerFloors(objects, viewerId, floors) {
    let byViewer = memo.get(objects);
    if (!byViewer) {
        byViewer = new Map();
        memo.set(objects, byViewer);
    }
    const key = `${viewerId}|${floors.map((f) => f.id).join(',')}`;
    const hit = byViewer.get(key);
    if (hit)
        return hit;
    const out = new Set();
    for (const o of Object.values(objects))
        if (o.owners.includes(viewerId))
            out.add(floorOf(o, floors));
    // A player with no token yet (or a watcher) is at the ground floor, as at a scene with one floor.
    if (!out.size)
        out.add(groundFloor(floors));
    byViewer.set(key, out);
    return out;
}
export const squareKeyOf = (c) => `${c.i},${c.j}`;
/**
 * Whether two floors meet at a square (slice B): they are next to each other in the stack and the upper one is open air
 * there (`open`: the Sky hides nothing below it) or has an opening on that square.
 */
export function joinsAt(floors, a, b, at) {
    const ia = floors.findIndex((f) => f.id === a);
    const ib = floors.findIndex((f) => f.id === b);
    if (ia < 0 || ib < 0 || Math.abs(ia - ib) !== 1)
        return false;
    const upper = floors[Math.max(ia, ib)];
    return upper.open === true || (upper.openings ?? []).includes(squareKeyOf(at));
}
/** Whether a token standing on `at` is at an opening between the two floors: on it, or on a square next to it (a balcony's edge). */
export function atOpening(floors, a, b, at) {
    for (let dj = -1; dj <= 1; dj += 1)
        for (let di = -1; di <= 1; di += 1)
            if (joinsAt(floors, a, b, { i: at.i + di, j: at.j + dj }))
                return true;
    return false;
}
/** The floors next to one in the stack: below, then above. */
export function neighbourFloors(floors, id) {
    const at = floors.findIndex((f) => f.id === id);
    return [floors[at - 1]?.id, floors[at + 1]?.id].filter((x) => typeof x === 'string');
}
const seenMemo = new WeakMap();
/**
 * The floors a player sees INTO (slice B; design/32 §4): a floor next to one of their tokens' through an opening the
 * token stands at, or through open air. Not their own floors. Memoised on the objects record with the grid's key.
 */
export function seenFloors(objects, viewerId, floors, cellOf, gridKey) {
    let byViewer = seenMemo.get(objects);
    if (!byViewer) {
        byViewer = new Map();
        seenMemo.set(objects, byViewer);
    }
    const key = `${viewerId}|${gridKey}|${JSON.stringify(floors.map((f) => [f.id, f.open, f.openings]))}`;
    const hit = byViewer.get(key);
    if (hit)
        return hit;
    const mine = viewerFloors(objects, viewerId, floors);
    const out = new Set();
    for (const o of Object.values(objects)) {
        if (!o.owners.includes(viewerId))
            continue;
        const from = floorOf(o, floors);
        const at = cellOf(o);
        for (const n of neighbourFloors(floors, from))
            if (!mine.has(n) && atOpening(floors, from, n, at))
                out.add(n);
    }
    byViewer.set(key, out);
    return out;
}
/**
 * The floors a token standing on a square may step onto through an opening marked there (qa-runs#1240): the neighbours of
 * its own floor, each by id and name, and nothing else of them. A player holds only the floors their tokens are on, so
 * the server says this of the square a token of theirs has just reached, never of any other. Open air joins every
 * square, so it isn't offered.
 */
export function floorsJoinedAt(floors, from, at) {
    const key = squareKeyOf(at);
    const here = floors.findIndex((f) => f.id === from);
    return neighbourFloors(floors, from).flatMap((n) => {
        const there = floors.findIndex((f) => f.id === n);
        return (floors[Math.max(here, there)]?.openings ?? []).includes(key) ? [{ id: n, name: floors[there].name }] : [];
    });
}
/** The floors a player is sent: theirs, each with where it starts and its map; and the ones they see into, by name alone. None while the scene has one floor. */
export function playerFloors(floors, objects, viewerId, seen) {
    if (!floors || floors.length < 2)
        return undefined;
    const mine = viewerFloors(objects, viewerId, floors);
    return floors.filter((f) => mine.has(f.id) || seen?.has(f.id)).map((f) => (mine.has(f.id)
        ? { id: f.id, name: f.name, height: f.height, ceiling: f.ceiling, ...(f.open ? { open: true } : {}), base: floorBase(floors, f.id), ...(f.map ? { map: { ...f.map } } : {}), ...(f.openings ? { openings: [...f.openings] } : {}) }
        : { id: f.id, name: f.name, height: f.height, ceiling: f.ceiling, ...(f.open ? { open: true } : {}), base: floorBase(floors, f.id), seen: true }));
}
/** Whether a player is sent the scene's own map (A2): it is the ground floor's, so only while one of their tokens is on the ground floor. Always with one floor. */
export function sceneMapShown(scene, objects, viewerId) {
    if (!hasFloors(scene))
        return true;
    const floors = sceneFloors(scene);
    return viewerFloors(objects, viewerId, floors).has(groundFloor(floors));
}
/** Whether a picture is a map a player may fetch at this scene (A2): the scene's own while they are on the ground floor, or the map of a floor of theirs. For the asset route. */
export function mapForViewer(assetId, sceneMapId, scene, objects, viewerId) {
    if (sceneMapId === assetId && sceneMapShown(scene, objects, viewerId))
        return true;
    if (!hasFloors(scene))
        return false;
    const floors = sceneFloors(scene);
    const mine = viewerFloors(objects, viewerId, floors);
    return floors.some((f) => f.map?.id === assetId && mine.has(f.id));
}
/** Whether a player may see a thing on its floor: the scene has one floor, or the thing is on one of theirs, or (`seen`) on one they see into. */
export function onViewerFloor(o, viewerId, scope, seen) {
    if (!hasFloors(scope.scene) || !scope.objects)
        return true;
    const floors = sceneFloors(scope.scene);
    const at = floorOf(o, floors);
    return viewerFloors(scope.objects, viewerId, floors).has(at) || (seen?.(viewerId).has(at) ?? false);
}
/**
 * The floor a removed floor's things go to by default: the one below it, else the one above (design/32 §2). `removed`
 * is the old list's id; `kept` the new list.
 */
export function floorBelow(old, removed, kept) {
    const at = old.findIndex((f) => f.id === removed);
    for (let k = at - 1; k >= 0; k -= 1)
        if (kept.some((f) => f.id === old[k].id))
            return old[k].id;
    for (let k = at + 1; k < old.length; k += 1)
        if (kept.some((f) => f.id === old[k].id))
            return old[k].id;
    return groundFloor(kept);
}
/** "40 ft · Sky": a height in the system's unit and the floor's name (no floor name with one floor). */
export function heightLabel(squares, floorName, distance) {
    const h = distance ? `${Math.round(squares * distance.perSquare * 100) / 100} ${distance.unit}` : `${Math.round(squares * 10) / 10} ${squares === 1 ? 'square' : 'squares'}`;
    return floorName ? `${h} · ${floorName}` : h;
}
/** The walls on one floor (design/32 §1): a wall with no floor stands on the ground floor. Every wall while the scene has one floor. */
export function wallsOnFloor(walls, floor, scene) {
    if (!hasFloors(scene))
        return [...walls];
    const floors = sceneFloors(scene);
    const at = floorOf({ floor }, floors);
    return walls.filter((w) => floorOf(w, floors) === at);
}
export const STAIRS_ONLY = 'A token changes floor only on a stair or an opening the DM marked.';
export const CEILING_IN_THE_WAY = 'The ceiling is in the way: a token rises through it only at an opening.';
/**
 * Why a player's move between floors or up is refused (slice B; design/32 §2), or null: a token changes floor only onto
 * the next floor, ending on a square where the two meet (a stair, an opening, open air); a floor's ceiling stops it rising
 * to the top of the floor except where the floor above opens onto it. The DM moves any token anywhere.
 */
export function floorMoveRefusal(floors, target, move, end, isDm) {
    if (isDm || floors.length < 2)
        return null;
    const from = floorOf(target, floors);
    if (move.floor !== undefined && move.floor !== from && !joinsAt(floors, from, move.floor, end))
        return STAIRS_ONLY;
    if (typeof move.z === 'number') {
        const on = move.floor ?? from;
        const at = floors.findIndex((f) => f.id === on);
        const f = floors[at];
        const above = floors[at + 1];
        if (f?.ceiling && move.z >= floorBase(floors, on) + f.height && !(above && joinsAt(floors, on, above.id, end)))
            return CEILING_IN_THE_WAY;
    }
    return null;
}
//# sourceMappingURL=floors.js.map